import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  useGetProductCategoriesQuery,
  useSearchProductCategoriesMutation,
} from '@app/api/Products';
import type { ModelProductCategoriesSearchResult } from '@app/api/Products';
import { EDIT_PRODUCT_CATEGORY_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import {
  getProductCategoriesColumns,
  initialFilters,
} from '@app/pages/Warehouses/ProductCategories/data';
import type {
  UnsavedProductCategoriesFilters,
  ProductCategoriesFilters,
} from '@app/pages/Warehouses/ProductCategories/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useErrorHandler } from '@app/shared/hooks';

import type { PaginationProps } from 'antd';
import type { ChangeEventHandler } from 'react';

export const useProductCategories = () => {
  const navigate = useNavigate();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [filters, setFilters] = useState<ProductCategoriesFilters>(
    () => initialFilters
  );

  const {
    userAccessRights: {
      Products: { IsProductCategories, IsProductCategoryEdit },
    },
  } = useNewUserAccessRights();

  const [unsavedFilters, setUnsavedFilters] =
    useState<UnsavedProductCategoriesFilters>({
      SearchTerm: '',
    });

  const [searchProductCategories, { isLoading: isFetching, isUninitialized }] =
    useSearchProductCategoriesMutation();

  const [data, setData] = useState<ModelProductCategoriesSearchResult | null>(
    null
  );

  const onSearchHandler = useCallback(async () => {
    if (!IsProductCategories) return;

    try {
      const response = await searchProductCategories({
        ActiveBranchId,
        data: filters,
      }).unwrap();

      setData(response);
    } catch (error) {
      showNotification(error, 'error');
    }
  }, [
    IsProductCategories,
    ActiveBranchId,
    filters,
    searchProductCategories,
    showNotification,
  ]);

  const {
    data: productCategoriesData,
    isFetching: isProductCategoriesFetching,
    isLoading: isProductCategoriesLoading,
    isUninitialized: isProductCategoriesUninitialized,
    isError: isProductCategoriesError,
    error: productCategoriesError,
  } = useGetProductCategoriesQuery({
    ActiveBranchId,
  });

  useErrorHandler([
    {
      flag: isProductCategoriesError,
      error: productCategoriesError,
    },
  ]);

  const total = useMemo(() => data?.CountItems ?? 0, [data]);
  const tableData = useMemo(() => data?.Items ?? [], [data]);

  const { ItemsPerPage, Page } = filters;

  const range = useMemo(() => {
    const from = (Page - 1) * ItemsPerPage + 1;
    const to = Math.min(Page * ItemsPerPage, total);
    return { from, to };
  }, [Page, ItemsPerPage, total]);

  const onChangeSearchTerm: ChangeEventHandler<HTMLInputElement> = useCallback(
    ({ target: { value: SearchTerm } }) => {
      setUnsavedFilters((prev) => ({ ...prev, SearchTerm }));
    },
    []
  );

  const skipSearchTerm = useCallback(() => {
    setUnsavedFilters((prev) => ({ ...prev, SearchTerm: '' }));
    setFilters((prevFilters) => ({ ...prevFilters, Page: 1, SearchTerm: '' }));
  }, []);

  const onSearch = useCallback(() => {
    const { SearchTerm } = unsavedFilters;
    setFilters((prevFilters) => ({ ...prevFilters, Page: 1, SearchTerm }));
  }, [unsavedFilters]);

  const onPaginate: PaginationProps['onChange'] = useCallback(
    (Page: number) => {
      setFilters((prevFilters) => ({ ...prevFilters, Page }));
    },
    []
  );

  const onPageSizeChange = useCallback((ItemsPerPage: number) => {
    setFilters((prevFilters) => ({ ...prevFilters, ItemsPerPage, Page: 1 }));
  }, []);

  const onEdit = useCallback(
    (id: number) => {
      navigate(`${EDIT_PRODUCT_CATEGORY_PATH}?id=${id}`);
    },
    [navigate]
  );

  const columns = useMemo(
    () =>
      getProductCategoriesColumns({
        onEdit,
        IsProductCategoryEdit,
        productCategories: productCategoriesData,
      }),
    [onEdit, IsProductCategoryEdit, productCategoriesData]
  );

  const isPageLoading = useMemo(
    () =>
      isUninitialized ||
      (isFetching && !data) ||
      isProductCategoriesLoading ||
      isProductCategoriesUninitialized ||
      isProductCategoriesFetching,
    [
      isFetching,
      isUninitialized,
      data,
      isProductCategoriesLoading,
      isProductCategoriesUninitialized,
      isProductCategoriesFetching,
    ]
  );

  return {
    filters,
    unsavedFilters,
    onPaginate,
    range,
    onPageSizeChange,
    columns,
    total,
    tableData,
    onChangeSearchTerm,
    skipSearchTerm,
    onSearch,
    onSearchHandler,
    isPageLoading,
    isFetching,
    IsProductCategories,
  };
};
